import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SupabaseService } from '../auth/supabase.service';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { from, of, switchMap } from 'rxjs';
import { DEV_JWT_KEY } from '../auth/dev-auth.constants';

/**
 * Funktionaler HTTP Interceptor der den Auth-Token automatisch
 * an API-Requests anhaengt — aber NUR wenn:
 * 1. Der Request an unsere API geht (nicht an externe URLs)
 * 2. Noch kein Authorization-Header gesetzt ist (manuell gesetzte Header haben Vorrang)
 *
 * Prioritaet:
 * 1. Supabase-Token (wenn Supabase-Session aktiv)
 * 2. MSAL-Token (wenn Microsoft-Account vorhanden, z.B. SystemAdmin im Email-Modus)
 *
 * Muss NACH dem MsalInterceptor registriert werden,
 * damit MSAL-Tokens nicht ueberschrieben werden.
 */
export const supabaseTokenInterceptor: HttpInterceptorFn = (req, next) => {
  // Nur API-Requests abfangen
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  // Wenn bereits ein Auth-Header da ist, nicht ueberschreiben
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  // Supabase-Token anhaengen wenn vorhanden
  const supabaseService = inject(SupabaseService);
  const supabaseToken = supabaseService.accessToken;
  if (supabaseToken) {
    const cloned = req.clone({
      setHeaders: { Authorization: `Bearer ${supabaseToken}` }
    });
    return next(cloned);
  }

  // Fallback: MSAL-Token holen (fuer SystemAdmin im Email-Modus)
  const msalService = inject(MsalService);
  const accounts = msalService.instance.getAllAccounts();
  if (accounts.length > 0) {
    return from(
      msalService.acquireTokenSilent({
        scopes: environment.msalConfig.scopes,
        account: accounts[0]
      })
    ).pipe(
      switchMap(result => {
        if (result.accessToken) {
          const cloned = req.clone({
            setHeaders: { Authorization: `Bearer ${result.accessToken}` }
          });
          return next(cloned);
        }
        // MSAL hat kein Token – dev_jwt als letzten Fallback versuchen
        return of(result).pipe(switchMap(() => {
          if (!environment.production) {
            const devJwt = localStorage.getItem(DEV_JWT_KEY);
            if (devJwt) {
              const cloned = req.clone({
                setHeaders: { Authorization: `Bearer ${devJwt}` }
              });
              return next(cloned);
            }
          }
          return next(req);
        }));
      })
    );
  }

  // Letzter Fallback: dev_jwt in non-Production
  if (!environment.production) {
    const devJwt = localStorage.getItem(DEV_JWT_KEY);
    if (devJwt) {
      const cloned = req.clone({
        setHeaders: { Authorization: `Bearer ${devJwt}` }
      });
      return next(cloned);
    }
  }

  return next(req);
};
